import React, { CSSProperties, PropsWithChildren, ReactNode, useCallback, useMemo } from 'react';
import { styled } from 'styled-components';
import { fixedSectionStyles, SectionBase } from '../SectionBase/SectionBase';
import { COLORS, computeRemSize, STYLE } from '@ydistri/theme';
import { Title } from '../typography/Typography/Typography';
import { TextSizeType } from '../typography/Typography/typographyTypes';
import { OpenIcon } from '../icons/OpenIcon/OpenIcon';

export const SectionHeaderWrapper = styled.div`
  display: inline-flex;
  gap: 1rem;
  flex-direction: row;
  align-items: center;
  width: fit-content;
  white-space: nowrap;
  padding-right: 2rem;
  flex-shrink: 0;
`;

export const SectionHeaderIndicator = styled.div`
  margin-right: ${computeRemSize(15)};
  width: 1rem;
  height: 1rem;
  border-radius: 50%;
  background-color: ${COLORS.PRIMARY};
`;

interface SectionHeaderProps extends PropsWithChildren {
  headerSuffix?: ReactNode;
  headerTitleSize?: TextSizeType;
  collapsible?: boolean;
  open?: boolean;
  showBulletPoint?: boolean;
  setOpen?: React.Dispatch<React.SetStateAction<boolean>>;
}

export const SectionHeader: React.FC<SectionHeaderProps> = ({
  children,
  headerSuffix,
  headerTitleSize = 'large',
  collapsible = false,
  open = true,
  showBulletPoint = true,
  setOpen,
}) => {
  const toggleOpenHandler = useCallback(() => setOpen?.(p => !p), [setOpen]);

  return (
    <SectionHeaderWrapper data-type="SectionHeader">
      {!collapsible && showBulletPoint && <SectionHeaderIndicator />}
      {collapsible && <OpenIcon $isOpened={open} $canBeOpened={true} onClick={toggleOpenHandler} />}
      <Title $size={headerTitleSize} $semiBold>
        {children}
      </Title>
      {headerSuffix}
    </SectionHeaderWrapper>
  );
};

export interface SectionProps {
  id?: string;
  children?: ReactNode;

  collapsible?: boolean;
  open?: boolean;
  onOpen?: (v: boolean) => void;
  showBulletPoint?: boolean;

  header?: string | ReactNode;
  /** Rendered next to a string header's title; custom node headers own their layout. */
  headerSuffix?: ReactNode;
  headerTitleSize?: TextSizeType;
  headerActions?: ReactNode;
  headerActionsAlignItems?: CSSProperties['alignItems'];
  headerActionsJustifyContent?: CSSProperties['justifyContent'];
  headerActionsIncludeMarginBottom?: boolean;
  contentActions?: ReactNode;

  $fixed?: boolean;
  $ratio?: number;
  $shrink?: number;
  $basis?: number;
  $scrollableContent?: boolean;
  /* True will add 16 px in rem size of padding to the header */
  $headerMarginTop?: boolean;
  /* True will add 1rem margin at the top of the content */
  $contentMarginTop?: boolean;
  $width?: string;
  $minWidth?: string;
  $height?: string;
  $minHeight?: string;
  $backgroundColor?: string;
}

type SectionContentProps = Pick<SectionProps, '$scrollableContent' | '$contentMarginTop'> & {
  $collapsible?: boolean;
  $open?: boolean;
};

const StyledSection = styled(SectionBase)<SectionProps>`
  flex-direction: column;
  flex-grow: ${({ $ratio }) => $ratio ?? 1};
  ${({ $shrink }) => typeof $shrink === 'number' && `flex-shrink: ${$shrink};`};
  ${({ $basis }) => $basis && `flex-basis: calc(${$basis}% - 1rem);`};
  padding-bottom: 0.5rem;
  padding-top: ${p => (p.$headerMarginTop ? computeRemSize(16) : '0.5rem')};
  ${({ $scrollableContent }) => {
    if (typeof $scrollableContent !== 'undefined') {
      if ($scrollableContent) {
        return ' min-height: 0';
      }
    }
  }};
  ${({ $width }) => $width && `width: ${$width};`};
  ${({ $minWidth }) => $minWidth && `min-width: ${$minWidth};`};
  ${({ $height }) => $height && `height: ${$height};`};
  ${({ $minHeight }) => $minHeight && `min-height: ${$minHeight};`};
  ${({ $backgroundColor }) => $backgroundColor && `background-color: ${$backgroundColor};`};
  ${({ $fixed }) => $fixed && fixedSectionStyles}
`;

export const SectionContent = styled.div<SectionContentProps>`
  padding: 0 1rem 0 1rem;
  margin-top: ${p => (p.$contentMarginTop ? '1rem' : 0)};
  display: flex;
  flex-direction: column;
  gap: 1rem;
  flex-grow: 1;

  ${({ $collapsible, $open }) => $collapsible && !$open && `display: none;`};
  ${({ $scrollableContent }) => $scrollableContent && `overflow-y: auto;${STYLE.SCROLLBAR}`}
`;

export const SectionDivider = styled.div<{ $modal?: boolean }>`
  border-bottom: 1px solid ${COLORS.BORDER};
  height: 1px;
  margin: ${p =>
    p.$modal ? `0 -${computeRemSize(24)} 0 -${computeRemSize(24)}` : `0 -1rem 0 -1rem`};
`;

/**
 * Common behaviour for header and content actions
 */
const ActionWrapper = styled.div`
  display: flex;
  align-items: center;
  padding-right: 1rem;
  gap: 1rem;
`;

/**
 * Display header on the left and actions on the right
 */
export const SectionHeaderWithActionsWrapper = styled(ActionWrapper)<{
  $headerActionAlignItems?: CSSProperties['alignItems'];
  $headerActionJustifyContent?: CSSProperties['justifyContent'];
  $includeMarginBottom?: boolean;
}>`
  justify-content: ${props => props.$headerActionJustifyContent ?? 'space-between'};
  align-items: ${p => p.$headerActionAlignItems ?? 'flex-start'};
  margin-bottom: ${props => (props.$includeMarginBottom ? '.5rem' : '0')};
  margin-left: ${computeRemSize(16)};
`;

/**
 * Display content actions on the right
 */
const ContentActionsWrapper = styled(ActionWrapper)`
  justify-content: flex-start;
  padding-left: 1rem;
`;

export const Section = ({
  children,
  header,
  headerSuffix,
  headerTitleSize = 'large',
  collapsible = false,
  open,
  onOpen,
  showBulletPoint = true,
  $ratio,
  $basis,
  $shrink,
  $scrollableContent,
  headerActions,
  headerActionsAlignItems,
  headerActionsJustifyContent,
  headerActionsIncludeMarginBottom = true,
  contentActions,
  $contentMarginTop = true,
  $headerMarginTop = true,
  ...rest
}: SectionProps): React.JSX.Element => {
  const isControlled = open !== undefined;

  //internal open state
  const [openState, setOpenState] = React.useState(() => {
    // initial default:
    // - if collapsible, default to provided open if any, otherwise true
    // - if not collapsible, always open
    if (!collapsible) return true;
    return open ?? true;
  });

  const effectiveOpen = isControlled ? open : openState;

  const toggleOpenHandler = useCallback(() => {
    if (isControlled) {
      onOpen?.(!open);
    } else {
      setOpenState(prev => {
        const next = !prev;
        onOpen?.(next);
        return next;
      });
    }
  }, [isControlled, onOpen, open]);

  const renderedHeader = useMemo(() => {
    if (!header && !headerActions) return null;

    let headerElement;
    if (header) {
      if (typeof header === 'string') {
        headerElement = (
          <SectionHeader
            data-type="SectionHeader"
            open={effectiveOpen}
            collapsible={collapsible}
            setOpen={toggleOpenHandler}
            showBulletPoint={showBulletPoint}
            headerTitleSize={headerTitleSize}
            headerSuffix={headerSuffix}
          >
            {header}
          </SectionHeader>
        );
      } else {
        headerElement = header;
      }
    }

    return (
      <SectionHeaderWithActionsWrapper
        data-type="SectionHeaderWithActions"
        $headerActionAlignItems={headerActionsAlignItems}
        $headerActionJustifyContent={headerActionsJustifyContent}
        $includeMarginBottom={headerActionsIncludeMarginBottom}
      >
        {headerElement}
        {headerActions ?? null}
      </SectionHeaderWithActionsWrapper>
    );
  }, [
    header,
    headerSuffix,
    headerActions,
    headerActionsAlignItems,
    headerActionsJustifyContent,
    headerActionsIncludeMarginBottom,
    effectiveOpen,
    collapsible,
    toggleOpenHandler,
    showBulletPoint,
    headerTitleSize,
  ]);

  return (
    <StyledSection
      $ratio={$ratio}
      $basis={$basis}
      $shrink={$shrink}
      data-type="Section"
      $scrollableContent={$scrollableContent}
      $headerMarginTop={$headerMarginTop}
      aria-expanded={effectiveOpen}
      {...rest}
    >
      {renderedHeader}
      {contentActions && <ContentActionsWrapper>{contentActions}</ContentActionsWrapper>}
      {children && (
        <SectionContent
          $contentMarginTop={$contentMarginTop}
          $scrollableContent={$scrollableContent}
          $collapsible={collapsible}
          $open={effectiveOpen}
          data-type="SectionContent"
        >
          {children}
        </SectionContent>
      )}
    </StyledSection>
  );
};

export const HalfScreenSection = styled(Section)`
  width: 49%;
  max-width: 49%;
`;
